import { CommonDialogs } from "@mcleod/common";
import { Map, Snackbar, TableRow } from "@mcleod/components";
import { GoogleMap } from "@mcleod/components/src/components/map/GoogleMap";
import { TrimbleMap } from "@mcleod/components/src/components/map/TrimbleMap";
import { LogManager, MapSettings, ModelRow, UserSettings } from "@mcleod/core";
import { GeographicZoneInput, MapZoneUtil, WebMapVendor, ZoneMapScreen } from "./MapZoneUtil";

const log = LogManager.getLogger("MapZoneSelectionUtil");

type GooglePolygonRef = { vendor: 'google'; polygons: google.maps.Polygon[] };
type TrimblePolygonRef = { vendor: 'trimble'; polygons: TrimblePolygonData[] };
type TrimblePolygonData = { polyId: string; layerIds: string[]; };
type CurrentPolygonRef = GooglePolygonRef | TrimblePolygonRef | null;

export class MapZoneSelectionUtil {

    private currentPolygon: CurrentPolygonRef = null;
    private mapVendor: string;
    private mapRoute: Map;

    constructor(mapVendor: string, mapRoute: Map) {
        this.mapVendor = mapVendor;
        this.mapRoute = mapRoute;
    }

    public async handleRowSelection(operation: ZoneMapScreen,tableRow: TableRow | null | undefined) {
        if (!tableRow) {
            const map = this.mapInstance(this.mapVendor);
            if (map) this.clearPolygon(map);
            return;
        }
        const modelRow = tableRow.data as ModelRow;
        const rowData = this.buildRowData(operation, modelRow);
        await this.handleSelection(operation, rowData);
    }

    private buildRowData(operation: ZoneMapScreen, modelRow: ModelRow) {
        if (!modelRow) return null;
        if (operation === ZoneMapScreen.ZipZone) {
            return { zip_code: modelRow.get("zipcode"), zone_id: modelRow.get("zone_id")};
        }
        return { id: modelRow.get("id") };
    }

    async handleSelection(operation: ZoneMapScreen, rowData: any) {

        if (!rowData) {
            Snackbar.showWarningSnackbar("Map Input Data cannot be null or empty");
            return;
        }

        if (MapZoneUtil.validateZoneMapVendor(this.mapVendor) === false) {
            return;
        }

        const input: GeographicZoneInput = { operation: operation, map_vendor: this.mapVendor, map_data: rowData };

        if (this.mapVendor === WebMapVendor.GoogleMaps) {
            await this.renderGoogleMapPolygon(input);
        } else if (this.mapVendor === WebMapVendor.TrimbleMaps) {
            await this.renderTrimbleMapPolygon(input);
        }

    }

    private async renderGoogleMapPolygon(input: GeographicZoneInput) {

        try {

            const googleMap = this.mapRoute.map as GoogleMap;
            const map = googleMap?.map as google.maps.Map;

            if (!map) {
                Snackbar.showWarningSnackbar("Please activate the map before selecting a zone.");
                return;
            }

            const data = await MapZoneUtil.getGeographicZoneData(input);

            if (!data || data.success === false) {
                this.clearPolygon(map);
                Snackbar.showWarningSnackbar(data?.message || "No polygon data found.");
                return;
            }

            this.clearPolygon(map);

            const userGoogleMapTypeId = MapSettings.getSingleton().userGoogleMapTypeId();
            const mapTypeId: google.maps.MapTypeId = this.getGoogleMapTypeId(userGoogleMapTypeId);
            const showTraffic: string = UserSettings.get()?.google_map_layer ?? "N";
            map.setMapTypeId(mapTypeId);
            if (showTraffic === "Y") {
                const trafficLayer = googleMap?.googleTrafficLayer as google.maps.TrafficLayer;
                if (trafficLayer) {
                    trafficLayer.setMap(map);
                }
            }

            map.setCenter(new google.maps.LatLng(data.latitude, data.longitude));
            const operation: string = input.operation;
            if (ZoneMapScreen.DispatchZone === operation) {
                const zoom_level = data.zoom_level + 1;
                map.setZoom(zoom_level);
            }
            else if (["ZR", "ZA", "ZS"].includes(operation)) {
                const zoom_level = data.zoom_level + 2;
                map.setZoom(zoom_level);
            } else {
                map.setZoom(data.zoom_level ?? 8);
            }

            const googlePolygonData = data.polygon_data;
            const polygons: google.maps.Polygon[] = [];
            const zoneCoords = [];

            for (let i = 0; i < googlePolygonData.length; i++) {
                zoneCoords[i] = googlePolygonData[i];
                const polygon = MapZoneUtil.createGooglePolygon(zoneCoords, i);
                polygon.setMap(map);
                polygons.push(polygon);
            }

            this.currentPolygon = { vendor: 'google', polygons };
        } catch (err) {
            CommonDialogs.showError("An error occurred while rendering the map polygon.");
            log.error(this, "Error rendering Google polygon:", err);
        }
    }

    private getGoogleMapTypeId(userGoogleMapTypeId: string): google.maps.MapTypeId {
        const type = userGoogleMapTypeId?.toUpperCase();
        switch (type) {
            case 'S': return google.maps.MapTypeId.SATELLITE;
            case 'T': return google.maps.MapTypeId.TERRAIN;
            case 'P': return google.maps.MapTypeId.HYBRID;
            default: return google.maps.MapTypeId.ROADMAP;
        }
    }

    private async renderTrimbleMapPolygon(input: GeographicZoneInput) {

        try {

            const trimbleMap = this.mapRoute.map as TrimbleMap;
            const map = trimbleMap?.map as TrimbleMaps.Map;

            if (!map) {
                Snackbar.showWarningSnackbar("Please activate the map before selecting a zone.");
                return;
            }

            const data = await MapZoneUtil.getGeographicZoneData(input);

            if (!data || data.success === false) {
                this.clearPolygon(map);
                Snackbar.showWarningSnackbar(data?.message || "No polygon data found.");
                return;
            }

            this.clearPolygon(map);

            const polygonData = data.polygon_data;
            const polygons: TrimblePolygonData[] = [];
            const zoomValue = data.zoom_level;
            const operation = input.operation;
            if ("ZZ" === operation || "MA" === operation) {
                const finalZoom = zoomValue != null ? (zoomValue < 6 ? 6 : zoomValue) : 6;
                map.setZoom(finalZoom);
            } else {
                map.setZoom(data.zoom_level ?? 6);
            }

            map.setCenter([data.longitude, data.latitude]);
            polygonData.forEach((polygonPoints: any, index: number) => {
                const polyId = `trimblePoly-${index}-${Math.random().toString(36).substr(2, 10)}`;
                const coordinates = polygonPoints.map((point: any) => [point.lng, point.lat]);
                const polygonGeoJson = MapZoneUtil.polygonDataTrimble(coordinates);
                map.addSource(polyId, { type: 'geojson', data: polygonGeoJson });
                const fillLayerId = `fill-${polyId}`;
                const outlineLayerId = `outline-${polyId}`;
                this.addFillAndOutlineLayer(map, fillLayerId, polyId, outlineLayerId);
                polygons.push({ polyId, layerIds: [fillLayerId, outlineLayerId] });
            });
            this.currentPolygon = { vendor: 'trimble', polygons };
        } catch (err) {
            log.error(this, "Error rendering Trimble polygon:", err);
        }
    }

    private addFillAndOutlineLayer(map, fillLayerId: string, polyId: any, outlineLayerId: string) {
        map.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: polyId,
            paint: { 'fill-color': '#ff0000', 'fill-opacity': 0.5 },
        });
        map.addLayer({
            id: outlineLayerId,
            type: 'line',
            source: polyId,
            paint: { 'line-color': '#000000', 'line-width': 1 },
        });
    }

    public mapInstance(mapVendor: string) {
        let mapInstance: any = null;
        if (mapVendor === WebMapVendor.GoogleMaps) {
            const googleMap = this.mapRoute.map as GoogleMap;
            mapInstance = googleMap?.map;
        } else if (mapVendor === WebMapVendor.TrimbleMaps) {
            const trimbleMap = this.mapRoute.map as TrimbleMap;
            mapInstance = trimbleMap?.map;
        }
        return mapInstance;
    }

    clearPolygon(map: any) {

        if (!this.currentPolygon) return;

        try {
            if (this.currentPolygon.vendor === 'google') {
                (this.currentPolygon as GooglePolygonRef).polygons.forEach(polygon => polygon.setMap(null));
                map.setCenter(new google.maps.LatLng(39.8283, -98.5795));
                map.setZoom(5);
            } else if (this.currentPolygon.vendor === 'trimble') {
                const trimbleRef = this.currentPolygon as TrimblePolygonRef;
                for (const poly of trimbleRef.polygons) {
                    for (const layerId of poly.layerIds) {
                        if (map.getLayer(layerId)) {
                            map.removeLayer(layerId);
                            log.debug(`Removed Trimble layer: ${layerId}`);
                        }
                    }

                    if (map.getSource(poly.polyId)) {
                        map.removeSource(poly.polyId);
                        log.debug(`Removed Trimble source: ${poly.polyId}`);
                    }
                }
                map.setCenter([-96, 35]);
                map.setZoom(3);
            }
        } catch (e) {
            log.error(this, "Error clearing polygon:", e);
        }

        this.currentPolygon = null;
    }
}
